import { useEffect } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { MotifButton, MotifModal } from '@ey-xd/motif-wc-react';
import { useClientStore } from '@/client/client-store';
import { clientIdFromPathname } from '@/router/client-navigation';

/**
 * Confirmation gate for a browser Back/Forward that would change the active
 * client. Port of the legacy `RouteDeactivateGuard`
 * (`raptor-shell-ui/src/app/core/guards/route-deactivate.guard.ts`): on a
 * `popstate` navigation whose target URL carries a DIFFERENT clientId than the
 * active one, the legacy shell popped the client-switch confirmation
 * (`EY_RAPToR_URLClientChange`) and only switched on "Yes". The React shell
 * instead switched silently in the route `beforeLoad`
 * (`persistLegacyRouteClient`), so the popup never showed.
 *
 * A dropdown switch is NOT caught here: it commits the new client to the store
 * BEFORE navigating, so the target clientId already equals the active one and
 * the block condition is false (the dropdown has its own `ClientSwitchModal`).
 *
 * On "Yes" the blocked POP proceeds and the route `beforeLoad` applies the
 * client switch (which refreshes the other tabs). On "No" the POP is reset and
 * the user stays on the current client + URL.
 */
export function ClientSwitchBackGuard() {
  const available = useClientStore((s) => s.available);
  const setSwitchPopupOpen = useClientStore((s) => s.setSwitchPopupOpen);

  const blocker = useBlocker({
    withResolver: true,
    enableBeforeUnload: false,
    shouldBlockFn: ({ next, action }) => {
      // Only history navigation (Back / Forward / Go — legacy `popstate`). A
      // PUSH/REPLACE is a dropdown or programmatic switch that already updated
      // the store, so its target clientId matches the active one anyway (and the
      // confirmation guards use REPLACE to resolve, so those must never block).
      if (action === 'PUSH' || action === 'REPLACE') return false;
      // Legacy `ValidateRouteGuard.canDeactivate` parity: while a client-switch
      // confirmation popup is open, block ANY Back/Forward so the user can't
      // navigate out of the pending decision (the modal overlay already blocks
      // in-page link clicks). Covers this guard and DirectClientChangeGuard.
      if (useClientStore.getState().isSwitchPopupOpen) return true;
      const currentId = useClientStore.getState().current?.id ?? null;
      if (!currentId) return false;
      const targetId = clientIdFromPathname(next.pathname);
      return targetId != null && targetId !== currentId;
    },
  });

  // Mirror the legacy `EY_RAPToR_IsClientSwitchPopUpOpen` flag so legacy MFEs /
  // guards that poll it see the popup state (and repeated back hits are ignored).
  useEffect(() => {
    setSwitchPopupOpen(blocker.status === 'blocked');
  }, [blocker.status, setSwitchPopupOpen]);

  if (blocker.status !== 'blocked') return null;

  const targetId = clientIdFromPathname(blocker.next.pathname);
  const target = targetId ? available.find((c) => c.id === targetId) : null;

  const confirm = () => {

    // Legacy parity (`RouteDeactivateGuard` -> `EY_RAPToR_URLClientChange(clientId)`;
    // `base-route.guard` sets `currentlySelectedClientId` to the URL's clientId):
    // a confirmed Back/Forward must ADOPT the URL's client. Commit it to the store
    // here — as the dropdown switch does before it navigates — because the route
    // `beforeLoad` (`persistLegacyRouteClient`) deliberately ignores a URL clientId
    // that differs from the active one (its MFE stale-write guard), so `proceed()`
    // alone would move the URL but never the store/localStorage. `setCurrent`
    // persists current/previous client ids and broadcasts the refresh to other tabs
    // ("ALL OPEN TABS refreshed"). When the previous client is no longer accessible
    // (`target` unresolved), skip the commit and let the client-access gate bounce.
    if (target) {
      useClientStore.getState().setCurrent(target, { source: 'url-change' });
    }
    blocker.proceed();
  };
  const cancel = () => {

    blocker.reset();
  };

  return (
    <div className="motif-theme-light">
      <MotifModal
        open
        onModalClose={cancel}
        headerTitle="Client Change"
        size="md"
        showFeaturedIcon={false}
        style={
          {
            '--modal-overflow-y': 'hidden',
            '--modal-body-overflow-y': 'hidden',
            '--modal-footer-width': '100%',
          } as React.CSSProperties
        }
      >
        <p slot="body">
          Please make sure to save any changes before changing the client,{' '}
          <b className="eyfontbold">ALL OPEN TABS</b> will be refreshed. Would you like to proceed?
        </p>
        <div slot="footer" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <MotifButton variant="secondary" onClick={cancel}>
              No
            </MotifButton>
            <MotifButton variant="primary" onClick={confirm}>
              Yes
            </MotifButton>
          </div>
        </div>
      </MotifModal>
    </div>
  );
}
